import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUser, useOrganization } from '@clerk/clerk-react';
import { FiAlertTriangle, FiUsers, FiX, FiHome } from 'react-icons/fi';

const OrganizationRouteGuard = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useUser();
  const { organization } = useOrganization();
  const [showWarning, setShowWarning] = useState(false);
  const [hasOrganizations, setHasOrganizations] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [blockedRoute, setBlockedRoute] = useState('');

  // Routes that require an organization
  const protectedRoutes = ['/dashboard', '/projects', '/system-resources', '/cost-analytics', '/query-analytics', '/log-analytics', '/api-keys'];
  
  // Routes that are always allowed
  const allowedRoutes = ['/', '/organization'];

  // Helper function to convert route paths to display names
  const getRouteDisplayName = (route) => {
    if (route.includes('/dashboard')) return 'Dashboard';
    if (route.includes('/projects')) {
      if (route.includes('/runhistory')) return 'Run History';
      if (route.includes('/projects/') && !route.includes('/runhistory')) return 'Project Details';
      return 'Projects';
    }
    if (route.includes('/system-resources')) return 'System Resources';
    if (route.includes('/cost-analytics')) return 'Cost Analytics';
    if (route.includes('/query-analytics')) return 'Query Analytics';
    if (route.includes('/log-analytics')) return 'Log Analytics';
    if (route.includes('/api-keys')) return 'API Keys';
    return route; // fallback to original route if no match
  };

  useEffect(() => {
    const checkUserOrganizations = async () => {
      if (!user) {
        setHasOrganizations(false);
        setIsChecking(false);
        return;
      }

      try {
        const memberships = await user.getOrganizationMemberships();
        const hasOrgs = memberships.data && memberships.data.length > 0;
        setHasOrganizations(hasOrgs);
        
        // Check if current route requires organization
        const currentPath = location.pathname;
        const isProtectedRoute = protectedRoutes.some(route => currentPath.startsWith(route));
        const isAllowedRoute = allowedRoutes.some(route => currentPath.startsWith(route));
        
        if (isProtectedRoute && !hasOrgs && !organization) {
          setBlockedRoute(currentPath);
          setShowWarning(true);
        } else if (isAllowedRoute) {
          setShowWarning(false);
        }
      } catch (error) {
        console.error('Error checking user organizations:', error);
        setHasOrganizations(false);
        
        // Check if current route requires organization
        const currentPath = location.pathname;
        const isProtectedRoute = protectedRoutes.some(route => currentPath.startsWith(route));
        
        if (isProtectedRoute && !organization) {
          setBlockedRoute(currentPath);
          setShowWarning(true);
        }
      } finally {
        setIsChecking(false);
      }
    };

    checkUserOrganizations();
  }, [user, organization, location.pathname]);



  const handleDismiss = () => {
    setShowWarning(false);
    navigate('/organization');
  };

  // Don't render anything while checking
  if (isChecking) {
    return children;
  }

  return (
    <>
      {children}
      
      {/* Organization Required Modal */}
      {showWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    <FiAlertTriangle className="h-5 w-5 text-red-600" />
                  </div>
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-800">Access Restricted</h2>
                  <p className="text-sm text-gray-500">Organization membership required</p>
                </div>
              </div>
              <button
                onClick={handleDismiss}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FiX size={24} />
              </button>
            </div>
            
            <div className="p-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiUsers className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Organization Required
                </h3>
                <p className="text-gray-600 mb-4">
                  You need to be part of an organization to access <strong>{getRouteDisplayName(blockedRoute)}</strong>.
                </p>
              </div>

              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center space-x-2">
                  <FiHome className="h-4 w-4 text-blue-600" />
                                      <span className="text-sm font-medium text-blue-800">
                      You'll be redirected to Organizations
                    </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OrganizationRouteGuard; 